import { useState } from 'react';
import { alterarSenhaDeUsuario } from '../../api/usuarios.js';
import type { UsuarioDoPainel } from '../../api/usuarios.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoTexto } from '../../componentes/Campo.js';
import { FormularioLateral } from '../../componentes/FormularioLateral.js';
import { seHouver } from '../../componentes/props.js';

type Props = {
  readonly usuario: UsuarioDoPainel;
  readonly ehProprio: boolean;
  readonly aoFechar: () => void;
  readonly aoConcluir: (mensagem: string) => void;
};

/** Toda troca derruba as outras sessões — a tela diz quantas caíram. */
function relatoDasSessoes(quantas: number): string {
  if (quantas === 0) {
    return 'Nenhuma sessão estava aberta.';
  }

  return quantas === 1 ? '1 sessão foi encerrada.' : `${String(quantas)} sessões foram encerradas.`;
}

/**
 * Define a senha de um usuário do painel.
 *
 * A própria senha exige a atual; o Administrador redefine a de terceiros sem
 * ela. Os dois campos são `password` e nada disso passa pela URL nem fica em
 * armazenamento nenhum do navegador.
 */
export function FormularioDeSenha({ usuario, ehProprio, aoFechar, aoConcluir }: Props) {
  const [senhaAtual, definirSenhaAtual] = useState('');
  const [novaSenha, definirNovaSenha] = useState('');
  const [erros, definirErros] = useState<Readonly<Record<string, string>>>({});
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  function confirmar() {
    const encontrados: Record<string, string> = {};

    if (ehProprio && senhaAtual === '') {
      encontrados['atual'] = 'Para trocar a própria senha, informe a senha atual.';
    }
    if (novaSenha === '') {
      encontrados['nova'] = 'Informe a nova senha.';
    }

    definirErros(encontrados);

    if (Object.keys(encontrados).length > 0) {
      return;
    }

    definirErro(null);
    definirEnviando(true);

    alterarSenhaDeUsuario(usuario.id, ehProprio ? { senhaAtual, novaSenha } : { novaSenha })
      .then((resultado) => {
        aoConcluir(
          `Senha de ${usuario.usuario} alterada. ${relatoDasSessoes(resultado.sessoesEncerradas)}`,
        );
      })
      .catch((falha: unknown) => {
        definirEnviando(false);
        definirErro(mensagemDoErro(falha));
      });
  }

  return (
    <FormularioLateral
      titulo={`Senha de ${usuario.usuario}`}
      subtitulo="Trocar a senha encerra as outras sessões deste usuário na hora."
      rotuloConfirmar="Alterar senha"
      aoConfirmar={confirmar}
      aoFechar={aoFechar}
      enviando={enviando}
      {...seHouver('erro', erro)}
    >
      {ehProprio && (
        <CampoTexto
          rotulo="Senha atual"
          valor={senhaAtual}
          aoMudar={definirSenhaAtual}
          tipo="password"
          obrigatorio
          autoCompletar="current-password"
          {...seHouver('erro', erros['atual'])}
        />
      )}
      <CampoTexto
        rotulo="Nova senha"
        valor={novaSenha}
        aoMudar={definirNovaSenha}
        tipo="password"
        obrigatorio
        autoCompletar="new-password"
        {...seHouver(
          'ajuda',
          ehProprio
            ? null
            : 'O Administrador redefine a senha de outra pessoa sem informar a atual.',
        )}
        {...seHouver('erro', erros['nova'])}
      />
    </FormularioLateral>
  );
}
